import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import type { TrainingBudget } from '@/types/budgets'
import {
  formatCurrency,
  formatPercent,
  calculateVariance,
  calculateUtilizationPercent,
} from '@/types/budgets'
import { updateTrainingBudget } from '@/lib/services/budgets.service'
import { toast } from 'sonner'

/**
 * Training Budget Form Validation Schema
 *
 * Validation rules:
 * - budget: Must be greater than 0
 * - actualCost: Must be greater than or equal to 0
 */
const trainingBudgetSchema = z.object({
  budget: z.number().positive('Training budget must be greater than 0'),
  actualCost: z.number().min(0, 'Actual cost cannot be negative'),
})

type TrainingBudgetFormData = z.infer<typeof trainingBudgetSchema>

interface TrainingBudgetCardProps {
  budget: TrainingBudget
  year: number
  onSave?: (updatedBudget: TrainingBudget) => void
}

/**
 * Training Budget Card Component
 *
 * Displays and validates training budget information with real-time calculations.
 *
 * Features:
 * - Two editable currency input fields (Training Budget, Actual Cost)
 * - Two calculated read-only fields (Variance, Utilization %)
 * - Color-coded progress bar based on utilization percentage
 * - Form validation using React Hook Form + Zod
 * - Save functionality with loading state and toast notifications
 * - Last updated metadata display
 *
 * @param budget - Training budget data from API
 * @param year - Budget year for display
 * @param onSave - Optional callback triggered after successful save
 */
export function TrainingBudgetCard({ budget, year, onSave }: TrainingBudgetCardProps) {
  // Initialize form with budget data
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<TrainingBudgetFormData>({
    resolver: zodResolver(trainingBudgetSchema),
    defaultValues: {
      budget: budget.budget,
      actualCost: budget.actualCost,
    },
    mode: 'onBlur', // Validate on blur per AC5 requirement
  })

  // Watch form values for real-time calculation
  const watchedBudget = watch('budget')
  const watchedActualCost = watch('actualCost')

  // Calculate variance and utilization in real-time
  const calculatedVariance = useMemo(() => {
    if (
      typeof watchedBudget === 'number' &&
      typeof watchedActualCost === 'number' &&
      !isNaN(watchedBudget) &&
      !isNaN(watchedActualCost)
    ) {
      return calculateVariance(watchedActualCost, watchedBudget)
    }
    return budget.variance
  }, [watchedBudget, watchedActualCost, budget.variance])

  const calculatedUtilizationPercent = useMemo(() => {
    if (
      typeof watchedBudget === 'number' &&
      typeof watchedActualCost === 'number' &&
      !isNaN(watchedBudget) &&
      !isNaN(watchedActualCost) &&
      watchedBudget > 0
    ) {
      return calculateUtilizationPercent(watchedActualCost, watchedBudget)
    }
    return budget.utilizationPercent
  }, [watchedBudget, watchedActualCost, budget.utilizationPercent])

  // Determine progress bar color based on utilization percentage
  const progressBarColor = useMemo(() => {
    if (calculatedUtilizationPercent < 80) {
      return 'bg-green-500'
    } else if (calculatedUtilizationPercent <= 100) {
      return 'bg-yellow-500'
    } else {
      return 'bg-red-500'
    }
  }, [calculatedUtilizationPercent])

  // Format metadata timestamp
  const formattedLastUpdated = useMemo(() => {
    if (!budget.metadata.lastUpdated) {
      return 'Never'
    }
    const date = new Date(budget.metadata.lastUpdated)
    return new Intl.DateTimeFormat('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }, [budget.metadata.lastUpdated])

  /**
   * Handle form submission
   * Saves training budget changes and displays appropriate feedback
   */
  const onSubmit = async (data: TrainingBudgetFormData) => {
    try {
      // Call API to update training budget
      const updatedBudget = await updateTrainingBudget(year, {
        budget: data.budget,
        actualCost: data.actualCost,
      })

      // Show success toast
      toast.success('Training budget updated successfully')

      // Trigger callback if provided (for refetching data)
      if (onSave) {
        onSave(updatedBudget)
      }
    } catch (error) {
      // Extract error message
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to update training budget. Please try again.'

      // Show error toast
      toast.error(errorMessage)

      // Log error for debugging
      console.error('Error updating training budget:', error)
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <h2 className="mb-4 text-xl font-semibold text-gray-900">
        Training Budget {year}
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Training Budget Field */}
        <div>
          <label htmlFor="training-budget" className="block text-sm font-medium text-gray-700">
            Training Budget
          </label>
          <div className="relative mt-1 rounded-md shadow-sm">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="text-gray-500 sm:text-sm">R$</span>
            </div>
            <input
              type="number"
              id="training-budget"
              {...register('budget', { valueAsNumber: true })}
              className={`block w-full rounded-md border-gray-300 py-2 pl-10 pr-12 focus-visible:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none sm:text-sm ${
                errors.budget ? 'border-red-300' : ''
              }`}
              placeholder="0.00"
              step="0.01"
              disabled={isSubmitting}
              aria-label="Orçamento de treinamento da empresa em reais brasileiros para o ano selecionado"
              aria-describedby={errors.budget ? 'training-budget-error' : undefined}
              aria-invalid={errors.budget ? 'true' : 'false'}
            />
          </div>
          {errors.budget && (
            <p id="training-budget-error" className="mt-1 text-sm text-red-600" role="alert">
              {errors.budget.message}
            </p>
          )}
        </div>

        {/* Actual Cost Field */}
        <div>
          <label htmlFor="actual-cost" className="block text-sm font-medium text-gray-700">
            Actual Cost
          </label>
          <div className="relative mt-1 rounded-md shadow-sm">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="text-gray-500 sm:text-sm">R$</span>
            </div>
            <input
              type="number"
              id="actual-cost"
              {...register('actualCost', { valueAsNumber: true })}
              className={`block w-full rounded-md border-gray-300 py-2 pl-10 pr-12 focus-visible:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none sm:text-sm ${
                errors.actualCost ? 'border-red-300' : ''
              }`}
              placeholder="0.00"
              step="0.01"
              disabled={isSubmitting}
              aria-label="Custo real de treinamento gasto em reais brasileiros para o ano selecionado"
              aria-describedby={errors.actualCost ? 'actual-cost-error' : undefined}
              aria-invalid={errors.actualCost ? 'true' : 'false'}
            />
          </div>
          {errors.actualCost && (
            <p id="actual-cost-error" className="mt-1 text-sm text-red-600" role="alert">
              {errors.actualCost.message}
            </p>
          )}
        </div>

        {/* Calculated Fields - Read-only */}
        <div className="space-y-4 rounded-md bg-gray-50 p-4">
          <h3 className="text-sm font-medium text-gray-700">Calculated Values</h3>

          {/* Variance */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Variance</span>
            <div className="flex items-center gap-2">
              {calculatedVariance < 0 ? (
                <>
                  <span className="text-green-600" aria-label="Under budget">
                    ↓
                  </span>
                  <span className="text-base font-semibold text-green-600">
                    {formatCurrency(calculatedVariance)}
                  </span>
                  <span className="text-xs text-gray-600">(Under budget)</span>
                </>
              ) : calculatedVariance > 0 ? (
                <>
                  <span className="text-red-600" aria-label="Over budget">
                    ↑
                  </span>
                  <span className="text-base font-semibold text-red-600">
                    {formatCurrency(calculatedVariance)}
                  </span>
                  <span className="text-xs text-gray-600">(Over budget)</span>
                </>
              ) : (
                <span className="text-base font-semibold text-gray-900">
                  {formatCurrency(calculatedVariance)}
                </span>
              )}
            </div>
          </div>

          {/* Utilization Percentage */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Utilization</span>
            <span className="text-base font-semibold text-gray-900">
              {formatPercent(calculatedUtilizationPercent, 1)}
            </span>
          </div>

          {/* Progress Bar */}
          <div>
            <div className="h-4 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className={`h-full transition-all duration-300 ${progressBarColor}`}
                style={{
                  width: `${Math.min(calculatedUtilizationPercent, 100)}%`,
                }}
                role="progressbar"
                aria-valuenow={calculatedUtilizationPercent}
                aria-valuemin={0}
                aria-valuemax={100}
              ></div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {calculatedUtilizationPercent < 80 && 'Under budget - good utilization'}
              {calculatedUtilizationPercent >= 80 &&
                calculatedUtilizationPercent <= 100 &&
                'Near budget limit'}
              {calculatedUtilizationPercent > 100 && 'Over budget!'}
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <svg
                  className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>

        {/* Metadata */}
        {budget.metadata.lastUpdated && (
          <div className="border-t border-gray-200 pt-4">
            <p className="text-sm italic text-gray-500">
              Last updated: {formattedLastUpdated}
              {budget.metadata.updatedBy && ` by ${budget.metadata.updatedBy}`}
            </p>
          </div>
        )}
      </form>
    </div>
  )
}
